import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface Row {
  id: string;
  account_handle: string;
  source_post_url: string;
  source_posted_at: string;
  neutral_summary: string;
  key_points: string[] | null;
  takeaway: string | null;
  summarized_at?: string | null;
  display_name?: string | null;
}

export function AnalystWatchX() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [upstreamDown, setUpstreamDown] = useState(false);


  const load = async () => {
    try {
      const [{ data: signals }, { data: accounts }, { data: sources }] = await Promise.all([
        supabase
          .from("x_social_signals")
          .select(
            "id, account_handle, source_post_url, source_posted_at, neutral_summary, key_points, takeaway, summarized_at",
          )
          .not("neutral_summary", "is", null)
          .order("source_posted_at", { ascending: false })
          .limit(30),
        supabase.from("tracked_x_accounts").select("handle, display_name"),
        supabase.from("analyst_sources").select("slug, name"),
      ]);
      const meta = new Map<string, string | null>();
      (accounts || []).forEach((a: any) => meta.set(a.handle, a.display_name));
      (sources || []).forEach((s: any) => meta.set(s.slug, s.name));
      const merged: Row[] = (signals || []).map((s: any) => ({
        ...s,
        key_points: Array.isArray(s.key_points) ? s.key_points : null,
        display_name: meta.get(s.account_handle) ?? null,
      }));
      setRows(merged);
      const newest = merged
        .map(r => r.summarized_at || r.source_posted_at)
        .filter(Boolean)
        .sort()
        .pop();
      setLastUpdated(newest ? new Date(newest) : null);
    } catch (e) {
      console.error("AnalystWatchX load error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    setUpstreamDown(false);
    try {
      // Fetch first, then paraphrase whatever is new.
      const { data, error } = await supabase.functions.invoke("fetch-analyst-rss");
      if (error || data?.success === false) {
        setUpstreamDown(true);
      } else {
        await supabase.functions.invoke("paraphrase-x-signals");
      }
      await load();
    } catch (e) {
      setUpstreamDown(true);
    } finally {
      setRefreshing(false);
    }
  };


  const items = useMemo(() => rows, [rows]);

  // Anything older than ~3 days means the upstream X source has stopped delivering.
  const isStale =
    !!lastUpdated && Date.now() - lastUpdated.getTime() > 3 * 24 * 60 * 60 * 1000;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageCircle className="w-5 h-5 text-primary" />
            Analyst Watch
            <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
              Research feeds
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span
                className={`text-xs ${isStale ? "text-amber-400" : "text-muted-foreground"}`}
                title={lastUpdated.toLocaleString()}
              >
                Newest item {formatDistanceToNow(lastUpdated, { addSuffix: true })}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Editorial summaries of what tracked analysts are saying — and what it reveals about their
          thinking. Not GoToCoin's analysis, and not a forecast.
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {upstreamDown && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
            <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-xs text-destructive">
              The research feeds didn't return anything just now, so nothing new could be added. Showing
              the last batch we received.
            </p>
          </div>
        )}

        {isStale && !loading && items.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/90">
              No new items have come through from the research feeds since{" "}
              {lastUpdated?.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}. What's
              below is the last batch we received, not today's commentary.
            </p>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No summarised items yet</p>
            <p className="text-xs mt-1 opacity-80">
              Nothing has come through from the research feeds yet. Try Refresh in a moment.
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {items.map(r => (
              <AnalystCard key={r.id} r={r} />
            ))}
          </div>
        )}

        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <AlertTriangle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Summaries are AI-generated from public posts and articles. We check the sources on a schedule,
            but items only appear when a source publishes something new — the timestamp above is the real age of the
            newest item, not a refresh promise. Not investment advice — analysts have their own agendas,
            paid products, and models with historical misses.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function AnalystCard({ r }: { r: Row }) {
  const posted = new Date(r.source_posted_at);
  const name = r.display_name || r.account_handle;

  return (
    <article className="p-4 rounded-lg border bg-secondary/30 border-border">
      <header className="flex items-baseline gap-2 flex-wrap mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {name}
        </span>

        <span className="text-[10px] text-muted-foreground ml-auto">
          {formatDistanceToNow(posted, { addSuffix: true })}
        </span>
      </header>

      <h3 className="text-base font-semibold leading-snug text-foreground mb-3">
        {r.neutral_summary}
      </h3>

      {r.key_points && r.key_points.length > 0 && (
        <ul className="space-y-1 mb-3">
          {r.key_points.map((p, i) => (
            <li
              key={i}
              className="text-sm text-foreground/85 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-muted-foreground"
            >
              {p}
            </li>
          ))}
        </ul>
      )}

      {r.takeaway && (
        <p className="text-sm italic text-primary/90 border-l-2 border-primary/40 pl-3 mb-3">
          {r.takeaway}
        </p>
      )}

      <footer className="text-[11px] text-muted-foreground">
        <a
          href={r.source_post_url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground hover:underline"
        >
          Read source
        </a>
      </footer>
    </article>
  );
}
